/**
 * Séries de points à aborder (migration 0247) — logique PURE partagée entre le
 * gestionnaire de séries et le résumé (apps/app) :
 *   - forme d'une série + schéma Zod du formulaire ;
 *   - modèles de départ (l'ancien prompt par type de rendez-vous, devenu 4 séries) ;
 *   - `buildSeriesInstructions` : les instructions envoyées à l'edge fn
 *     `visio-room-summarize` à partir d'une série ;
 *   - progression d'une checklist.
 * `packages/visio` ne dépend pas de `@jeraisonne/db` : il redéclare localement
 * la forme minimale qu'il lit.
 */

import { z } from 'zod';
import type { RendezVousKind } from '../types';

export const VISIO_SERIES_TITLE_MAX_LENGTH = 80;
export const VISIO_SERIES_POINT_MAX_LENGTH = 200;
export const VISIO_SERIES_POINTS_MAX = 25;
export const VISIO_SERIES_FORMAT_MAX_LENGTH = 800;

export interface VisioSeriesPoint {
  /** Identifiant STABLE : l'état « coché » d'une visio s'y accroche (renommer/réordonner ne décoche pas). */
  id: string;
  text: string;
}

export interface VisioSeries {
  id: string;
  title: string;
  points: VisioSeriesPoint[];
  /** Consigne de forme du résumé ; `null` = forme par défaut. */
  formatMd: string | null;
}

export const visioSeriesPointSchema = z.object({
  id: z.string().min(1).max(64),
  text: z
    .string()
    .trim()
    .min(1, 'Un point ne peut pas être vide')
    .max(VISIO_SERIES_POINT_MAX_LENGTH),
});

/** Formulaire de création/édition d'une série. */
export const visioSeriesInputSchema = z.object({
  title: z.string().trim().min(1, 'Donne un titre à la série').max(VISIO_SERIES_TITLE_MAX_LENGTH),
  points: z
    .array(visioSeriesPointSchema)
    .min(1, 'Ajoute au moins un point')
    .max(VISIO_SERIES_POINTS_MAX),
  formatMd: z
    .string()
    .trim()
    .max(VISIO_SERIES_FORMAT_MAX_LENGTH)
    .nullable()
    .optional()
    .transform((v) => (v ? v : null)),
});
export type VisioSeriesInput = z.infer<typeof visioSeriesInputSchema>;

/** Nouvel identifiant de point (uuid si disponible, sinon repli suffisant pour l'unicité locale). */
export function newSeriesPointId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return c?.randomUUID
    ? c.randomUUID()
    : `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Lit défensivement la colonne jsonb `points` (une ligne mal formée ne doit jamais casser l'écran). */
export function parseSeriesPoints(raw: unknown): VisioSeriesPoint[] {
  if (!Array.isArray(raw)) return [];
  const points: VisioSeriesPoint[] = [];
  for (const item of raw) {
    const { id, text } = (item ?? {}) as { id?: unknown; text?: unknown };
    if (typeof id === 'string' && id && typeof text === 'string' && text.trim()) {
      points.push({ id, text });
    }
  }
  return points;
}

export const DEFAULT_SERIES_FORMAT =
  'Format : une section par point (reprends son intitulé comme titre), réponses courtes en puces. Si la réponse n’est pas dans le dialogue, écris « Non abordé dans le dialogue ».';

/**
 * Instructions envoyées à l'IA : une série de points dont la réponse doit se
 * trouver dans le dialogue. Même mécanique que le prompt d'origine (validé en
 * conditions réelles) — seule la source change : la série choisie.
 */
export function buildSeriesInstructions(series: {
  title: string;
  points: ReadonlyArray<{ text: string }>;
  formatMd?: string | null;
}): string {
  const list = series.points.map((p, i) => `${i + 1}. ${p.text.trim()}`).join('\n');
  const format = series.formatMd?.trim() || DEFAULT_SERIES_FORMAT;
  return `Fais le compte-rendu de ce rendez-vous (série « ${series.title.trim()} »). Réponds à chacun des points suivants, uniquement à partir du dialogue :

${list}

${format}`;
}

/** Avancement d'une checklist — les ids cochés qui ne correspondent plus à aucun point (point supprimé) sont ignorés. */
export function seriesProgress(
  points: ReadonlyArray<{ id: string }>,
  checkedIds: ReadonlyArray<string>,
): { done: number; total: number } {
  const checked = new Set(checkedIds);
  return { total: points.length, done: points.filter((p) => checked.has(p.id)).length };
}

export interface DefaultVisioSeries {
  /** Type de rendez-vous auquel le modèle correspond (indicatif). */
  kind: RendezVousKind;
  title: string;
  points: string[];
}

/** Séries de départ (ex-prompts par type de rendez-vous), proposées à la 1re ouverture. */
export const DEFAULT_VISIO_SERIES: readonly DefaultVisioSeries[] = [
  {
    kind: 'cours',
    title: 'Cours particulier',
    points: [
      'Quelles notions ou quels exercices ont été travaillés ?',
      'Quelles difficultés ou erreurs de l’élève sont apparues ?',
      'Qu’est-ce qui semble acquis ?',
      'Quel travail est à faire avant la prochaine séance ?',
      'Quels points de vigilance pour la suite ?',
    ],
  },
  {
    kind: 'etude',
    title: 'Session d’étude',
    points: [
      'Quelles notions ou quels exercices ont été travaillés ?',
      'Quelles difficultés ou erreurs sont apparues ?',
      'Qu’est-ce qui semble acquis ?',
      'Quel travail est à faire avant la prochaine session ?',
    ],
  },
  {
    kind: 'express',
    title: 'Appel rapide',
    points: [
      'Quel était l’objet de l’appel ?',
      'Quelle réponse ou quelle décision a été donnée ?',
      'Y a-t-il une action à suivre (qui, quoi, pour quand) ?',
    ],
  },
  {
    kind: 'entretien',
    title: 'Entretien de suivi',
    points: [
      'Quels thèmes ont été abordés, et dans quel ordre ?',
      'Où en est l’étudiant (avancement, difficultés rencontrées) ?',
      'Quels retours de l’entreprise ou de l’école ont été évoqués ?',
      'Quels objectifs ou engagements ont été pris (qui, quoi, pour quand) ?',
      'Quelles sont les prochaines étapes (prochain rendez-vous, points à suivre) ?',
    ],
  },
];

/** Les séries de départ au format formulaire (ids générés) — prêtes à être insérées. */
export function defaultSeriesInputs(): VisioSeriesInput[] {
  return DEFAULT_VISIO_SERIES.map((s) => ({
    title: s.title,
    points: s.points.map((text) => ({ id: newSeriesPointId(), text })),
    formatMd: null,
  }));
}
